import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import Loading from "../../../Components/Loading";
import CustomDataTable from "../../../Components/CustomDataTable";
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { CreateTag } from "../TagComponents/CreateTag";
import { CreateTagGroup } from "../TagComponents/CreateTagGroupDialog";
import { EditTagGroup } from "../TagComponents/EditTagGroupDialog";
import { EditTag } from "../TagComponents/EditTagDialog";
import {
    getTagDisplayName,
    getTagGroupsTotalElements,
    normalizeGraphedTagGroups,
    normalizeTagGroupsList,
    normalizeTagGroupsSearchResults,
    TAG_GROUP_TABLE_PAGE_SIZE,
} from "./tagUtils";

const noop = () => {};

const sortByCreatedDesc = (a, b) => {
    const aTime = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
    const bTime = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
    return bTime - aTime;
};

const TagsTab = ({ businessId }) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const {
        searchingTagGroups,
        tagGroupSearchResults,
        tagGroups,
        tagGroupListTotalElements,
        fetchingTagGroups,
        tagGroupsGraphed,
    } = useSelector((state) => state.businessCustomers);

    const searchTagGroups = useCallback(
        (id, name) => dispatch(BusinessCustomersActions.tagGroupSearchRequest(id, name)),
        [dispatch]
    );

    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [pageState, setPageState] = useState({
        page: 0,
        first: 0,
        size: TAG_GROUP_TABLE_PAGE_SIZE,
    });
    const [expandedRows, setExpandedRows] = useState(null);
    const [createTagVisible, setCreateTagVisible] = useState(false);
    const [createTagGroupVisible, setCreateTagGroupVisible] = useState(false);
    const [editTagGroupVisible, setEditTagGroupVisible] = useState(false);
    const [editTagVisible, setEditTagVisible] = useState(false);
    const [tagToEdit, setTagToEdit] = useState(null);
    const [tagGroupToEdit, setTagGroupToEdit] = useState(null);
    const [tagGroupValues, setTagGroupValues] = useState(null);
    const [editTagGroupInitial, setEditTagGroupInitial] = useState(null);

    useEffect(() => {
        if (!editTagVisible) {
            setTagToEdit(null);
            setTagGroupToEdit(null);
        }
    }, [editTagVisible]);

    const pageOptions = useMemo(
        () => ({
            page: pageState.page,
            size: pageState.size,
            sort: "createdDate,desc",
        }),
        [pageState.page, pageState.size]
    );

    const tagGroupsList = useMemo(() => normalizeTagGroupsList(tagGroups), [tagGroups]);

    const fetchTagGroupsPage = useCallback(
        (page, size = TAG_GROUP_TABLE_PAGE_SIZE) => {
            if (!businessId) {
                return;
            }
            dispatch(
                BusinessCustomersActions.tagGroupRequest(businessId, {
                    page,
                    size,
                    sort: "createdDate,desc",
                })
            );
        },
        [businessId, dispatch]
    );

    const loadGraphedTagGroups = useCallback(() => {
        if (businessId) {
            dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId));
        }
    }, [businessId, dispatch]);

    const refreshTagsData = useCallback(() => {
        loadGraphedTagGroups();
        fetchTagGroupsPage(pageState.page, pageState.size);
    }, [loadGraphedTagGroups, fetchTagGroupsPage, pageState.page, pageState.size]);

    useEffect(() => {
        if (businessId && !appliedSearch) {
            loadGraphedTagGroups();
            fetchTagGroupsPage(pageState.page, pageState.size);
        }
    }, [
        businessId,
        appliedSearch,
        loadGraphedTagGroups,
        fetchTagGroupsPage,
        pageState.page,
        pageState.size,
    ]);

    const isSearchMode = appliedSearch.length > 0;

    const tagGroupsWithTags = useMemo(() => {
        const graphedGroups = normalizeGraphedTagGroups(tagGroupsGraphed);
        const graphedById = new Map(graphedGroups.map((group) => [String(group.id), group]));

        const baseGroups = isSearchMode
            ? normalizeTagGroupsSearchResults(tagGroupSearchResults)
            : tagGroupsList;

        return [...baseGroups]
            .map((group) => {
                const withTags = {
                    ...group,
                    tags: group.tags ?? graphedById.get(String(group.id))?.tags ?? [],
                };
                const tags = [...(withTags.tags ?? [])]
                    .filter((tag) => tag?.id && getTagDisplayName(tag))
                    .sort(sortByCreatedDesc);
                return { ...withTags, tags };
            })
            .sort(sortByCreatedDesc);
    }, [tagGroupsGraphed, tagGroupSearchResults, tagGroupsList, isSearchMode]);

    const totalRecords = isSearchMode
        ? getTagGroupsTotalElements(tagGroupSearchResults)
        : tagGroupListTotalElements ?? getTagGroupsTotalElements(tagGroups);

    const tableLoading = isSearchMode ? searchingTagGroups : fetchingTagGroups;

    const onPageChange = (e) => {
        const isRowsChanged = pageState.size !== e.rows;
        if (isRowsChanged) {
            setPageState({ page: 0, first: 0, size: e.rows });
        } else {
            setPageState({ page: e.page, first: e.first, size: e.rows });
        }
    };

    const runTagSearch = useCallback(() => {
        const query = searchText.trim();
        setAppliedSearch(query);
        if (query && businessId) {
            searchTagGroups(businessId, query);
        }
    }, [searchText, businessId, searchTagGroups]);

    const clearTagSearch = useCallback(() => {
        setSearchText("");
        setAppliedSearch("");
        refreshTagsData();
    }, [refreshTagsData]);

    const expandTagGroupRow = (group) => {
        if (group?.id == null) {
            return;
        }
        setExpandedRows((prev) => ({ ...(prev || {}), [group.id]: true }));
    };

    const openCreateTagForGroup = (group) => {
        setTagGroupValues(group);
        expandTagGroupRow(group);
        setCreateTagVisible(true);
    };

    const refreshTags = useCallback(() => {
        refreshTagsData();
        if (appliedSearch && businessId) {
            searchTagGroups(businessId, appliedSearch);
        }
    }, [refreshTagsData, appliedSearch, businessId, searchTagGroups]);

    const handleTagsChanged = useCallback(
        (groupId) => {
            refreshTags();
            if (groupId != null) {
                setExpandedRows((prev) => ({ ...(prev || {}), [groupId]: true }));
            }
        },
        [refreshTags]
    );

    const reloadTagGroupsForEdit = useCallback(
        (id) => {
            dispatch(BusinessCustomersActions.tagGroupRequest(id, pageOptions));
            loadGraphedTagGroups();
        },
        [dispatch, pageOptions, loadGraphedTagGroups]
    );

    const openEditTagGroup = (group) => {
        setEditTagGroupInitial(group);
        setEditTagGroupVisible(true);
    };

    const openEditTag = (rowData, group) => {
        setTagToEdit(rowData);
        setTagGroupToEdit({
            ...group,
            tags: group.tags ?? [rowData],
        });
        setEditTagVisible(true);
    };

    const groupNameBody = (rowData) => (
        <div className="segments-name-cell">
            <span className="name-body">{rowData.name}</span>
        </div>
    );

    const groupActionsBody = (rowData) => (
        <div className="products-action-cell segments-action-cell-left">
            <div className="car-buttons table_action_button">
                <Button
                    type="button"
                    icon="pi pi-plus-circle"
                    className="p-button active-action-button"
                    id="action-button"
                    tooltip={t("tags.add_tag_to_group")}
                    tooltipOptions={{
                        position: "bottom",
                        mouseTrack: true,
                        mouseTrackTop: 15,
                    }}
                    onClick={() => openCreateTagForGroup(rowData)}
                />
                <Button
                    type="button"
                    icon="pi pi-pencil"
                    className="p-button active-action-button"
                    id="action-button"
                    tooltip={t("tags.edit_tag_group")}
                    tooltipOptions={{
                        position: "bottom",
                        mouseTrack: true,
                        mouseTrackTop: 15,
                    }}
                    onClick={() => openEditTagGroup(rowData)}
                />
            </div>
        </div>
    );

    const tagNameRowBody = (rowData) => (
        <div className="segments-name-cell tags-expanded-name-cell">
            <div className="tag-div-main">
                <div className="tag_div">
                    <span className="tag-class">{getTagDisplayName(rowData)}</span>
                </div>
            </div>
        </div>
    );

    const tagRowActionsBody = (rowData, group) => (
        <div className="products-action-cell segments-action-cell-left">
            <div className="car-buttons table_action_button">
                <Button
                    type="button"
                    icon="pi pi-pencil"
                    className="p-button active-action-button"
                    id="action-button"
                    tooltip={t("tags.edit_tag")}
                    tooltipOptions={{
                        position: "bottom",
                        mouseTrack: true,
                        mouseTrackTop: 15,
                    }}
                    onClick={() => openEditTag(rowData, group)}
                />
            </div>
        </div>
    );

    const rowExpansionTemplate = (group) => {
        const tags = (group.tags ?? []).filter((tag) => tag?.id && getTagDisplayName(tag));

        return (
            <div className="tags-expanded-panel">
                <h6 className="group-name products-plans-heading">{t("tags.group_tags_heading")}</h6>
                <DataTable
                    id="expanded-table"
                    className="p-datatable-responsive-table expanded tags-expanded-table"
                    rowClassName={() => "subscribers-data-table tags-expanded-row"}
                    value={tags}
                    responsiveLayout="stack"
                    breakpoint="1050px"
                    dataKey="id"
                    emptyMessage={t("tags.empty_group_tags_state")}
                >
                    <Column
                        alignHeader="left"
                        field="name"
                        header={t("common.name")}
                        body={tagNameRowBody}
                        className="datatable-cell-default segments-column-left tags-expanded-name-column"
                        headerClassName="datatable-header-default"
                    />
                    <Column
                        alignHeader="left"
                        field="actions"
                        header={t("common.actions")}
                        body={(rowData) => tagRowActionsBody(rowData, group)}
                        className="datatable-cell-default products-action-column tags-expanded-actions-column"
                        headerClassName="datatable-header-default"
                    />
                </DataTable>
            </div>
        );
    };

    const columns = [
        {
            expander: true,
            className: "datatable-cell-default products-mobile-expander chevron table_dropdown",
            headerClassName: "datatable-header-default header-title",
        },
        {
            field: "name",
            header: t("tags.tag_group_label"),
            body: groupNameBody,
            className: "segments-column-left products-name-column",
            bodyClassName: "segments-column-left",
            alignHeader: "left",
            align: "left",
        },
        {
            field: "actions",
            header: t("common.actions"),
            body: groupActionsBody,
            className: "products-action-column segments-column-left",
            bodyClassName: "segments-column-left",
            alignHeader: "left",
            align: "left",
        },
    ];

    const tableHeader = (
        <div className="segments-table-toolbar">
            <div className="segments-search">
                <div className="subscribers-search-input">
                    <InputText
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t("tags.search_tags_placeholder")}
                        className="w-full"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                runTagSearch();
                            }
                        }}
                    />
                    <Button
                        type="button"
                        onClick={runTagSearch}
                        icon="pi pi-search"
                        className="p-button-info p-button-text subscribers-search-btn"
                        aria-label={t("tags.search_tags_placeholder")}
                    />
                    {searchText ? (
                        <Button
                            type="button"
                            onClick={clearTagSearch}
                            icon="pi pi-times"
                            className="p-button-danger p-button-text subscribers-search-clear-btn"
                            aria-label={t("common.close")}
                        />
                    ) : null}
                </div>
            </div>
            <Button
                className="create_tag_btn tags-tab-create-group-btn"
                icon="pi pi-plus-circle"
                label={t("tags.create_tag_group")}
                onClick={() => setCreateTagGroupVisible(true)}
            />
        </div>
    );

    if (!isSearchMode && fetchingTagGroups && tagGroups == null) {
        return <Loading />;
    }

    return (
        <>
            <div className="customers-screen tags-groups-screen">
                <CustomDataTable
                    value={tagGroupsWithTags}
                    paginator={false}
                    responsiveLayout="stack"
                    columns={columns}
                    dataKey="id"
                    header={tableHeader}
                    tableClassName="segments-data-table tags-data-table products-data-table"
                    rowClassName={() => "subscribers-data-table segments-list-row"}
                    loading={tableLoading}
                    emptyMessage={t("tags.empty_tags_state")}
                    expandedRows={expandedRows}
                    rowExpansionTemplate={rowExpansionTemplate}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                />
                {!isSearchMode ? (
                    <Paginator
                        rows={pageState.size}
                        totalRecords={totalRecords ?? 0}
                        first={pageState.first}
                        onPageChange={onPageChange}
                        className="custom-paginator"
                    />
                ) : null}
            </div>

            <CreateTag
                visible={createTagVisible}
                hideTagPopup={() => {
                    setCreateTagVisible(false);
                    setTagGroupValues(null);
                }}
                pageOptions={pageOptions}
                getSummaryCustomers={noop}
                businessId={businessId}
                tagGroupValues={tagGroupValues}
                setTagGroupValues={setTagGroupValues}
                onTagsChanged={handleTagsChanged}
            />

            <CreateTagGroup
                visible={createTagGroupVisible}
                createTagGroupDisplay={createTagGroupVisible}
                hideTagGroupPopup={() => setCreateTagGroupVisible(false)}
                pageOptions={pageOptions}
                tagGroups={tagGroupsList}
                getSummaryCustomers={noop}
                businessId={businessId}
                onTagsChanged={refreshTags}
            />

            <EditTagGroup
                visible={editTagGroupVisible}
                hideEditTagGroupPopup={() => {
                    setEditTagGroupVisible(false);
                    setEditTagGroupInitial(null);
                }}
                pageOptions={pageOptions}
                tagGroups={tagGroupsList}
                getSummaryCustomers={noop}
                getTagGroups={reloadTagGroupsForEdit}
                businessId={businessId}
                initialTagGroup={editTagGroupInitial}
            />

            <EditTag
                visible={editTagVisible}
                hideEditTagPopup={() => setEditTagVisible(false)}
                pageOptions={pageOptions}
                getSummaryCustomers={noop}
                getTags={refreshTagsData}
                tagToEdit={tagToEdit}
                tagGroupToEdit={tagGroupToEdit}
                onTagsChanged={refreshTags}
            />
        </>
    );
};

export default TagsTab;
